import * as Lib from '../constants/annotator'

// Extract every saved median-beat window from the on-disk blob. Accepts
// either the v3 `medianBeats` object or the legacy `medianBeatFiducialPoints`
// blob — both nest windows at `byBeatType[<slug>].window`. Legacy v1
// (single-type object with a top-level `.window`) is migrated into the `n`
// bucket. Missing input returns `{}`.
export const extractMedianBeatWindowsByType = (raw) => {
    if (!raw) return {}
    if (raw.byBeatType) {
        const out = {}
        for (const [slug, entry] of Object.entries(raw.byBeatType)) {
            if (entry?.window) out[slug] = entry.window
        }
        return out
    }
    if (raw.window) return { n: raw.window }
    return {}
}

export const getMedianBeatFrequency = (annotatorState) => {
    // Median beats are upsampled to a common target rate before display so
    // pixel↔sample math has consistent, sub-2ms precision. Recordings whose
    // native rate already exceeds the target are left alone (downsampling
    // would only lose precision), so we take the max here.
    const leadCount = annotatorState?.leadCount
    const has12LeadMedianBeats = !!annotatorState?.rawData?.data?.kai12Analysis?.medianBeats
    const sourceHz = (leadCount === 12 && has12LeadMedianBeats)
        ? Lib.MEDIAN_BEAT_FREQUENCY_12_LEAD
        : (annotatorState?.filtered?.frequency || 0)
    return Math.max(sourceHz, Lib.MEDIAN_BEAT_TARGET_HZ)
}

// R-peak index within the current median-beat waveform (in samples).
// Non-12-lead: derived from the user-adjustable left window in ms.
// 12-lead: assumed to be the waveform midpoint (kai12 median beats are static).
export const getMedianBeatRPeakSample = (annotatorState, waveformLength) => {
    const leadCount = annotatorState?.leadCount
    const has12LeadMedianBeats = !!annotatorState?.rawData?.data?.kai12Analysis?.medianBeats
    if (leadCount === 12 && has12LeadMedianBeats) {
        return Math.floor((waveformLength || 0) / 2)
    }
    // R-peak is at `medianBeatLeftMs` after the start of the median-beat
    // window, in the rendered signal's sample space (max of source and target).
    const renderRate = getMedianBeatFrequency(annotatorState)
    const currentBeatType = annotatorState?.settings?.medianBeatBeatType || "n"
    const window = (annotatorState?.settings?.medianBeatWindowByType || {})[currentBeatType] || Lib.DEFAULT_MEDIAN_BEAT_WINDOW
    const leftMs = window.leftMs ?? Lib.DEFAULT_MEDIAN_BEAT_WINDOW_MS
    return Math.round((leftMs / 1000) * renderRate)
}

export const msToSample = (offsetMs, frequency, rPeakSample) => {
    if (!frequency) return rPeakSample
    return rPeakSample + Math.round((offsetMs / 1000) * frequency)
}

export const sampleToMs = (sample, frequency, rPeakSample) => {
    if (!frequency) return 0
    return ((sample - rPeakSample) / frequency) * 1000
}

// Signature of everything that could affect the median-beat waveform. If this
// changes after fiducial points were placed, the points reference a stale
// waveform and should not be persisted without user confirmation.
//
// For non-12-lead: the sorted list of beat sample indices matching the
// selected beat-type filter (which drive alignedBeatFromIndices), the
// filtered signal length (catches filter changes), and the inversion flag.
// The median-beat window (leftMs/rightMs) is intentionally NOT part of the
// signature — points are stored as ms offsets from the R-peak, so changing
// the window only affects which points are visible, not their placement
// accuracy. A separate prune step drops points that fall outside the window.
// For 12-lead: a probe of the static kai12Analysis.medianBeats so a change of
// underlying recording (e.g., re-upload) invalidates points. Beat-type does
// not affect the 12-lead median.
export const buildFiducialInputSignature = (annotatorState, beatTypeOverride) => {
    if (!annotatorState) return null
    const leadCount = annotatorState.leadCount
    const has12LeadMedianBeats = !!annotatorState.rawData?.data?.kai12Analysis?.medianBeats
    if (leadCount === 12 && has12LeadMedianBeats) {
        const mb = annotatorState.rawData.data.kai12Analysis.medianBeats
        const parts = mb.map((leadSamples, idx) => {
            if (!Array.isArray(leadSamples) || leadSamples.length === 0) return `${idx}:0`
            const probes = [0, Math.floor(leadSamples.length / 2), leadSamples.length - 1]
                .map(i => Number(leadSamples[i]).toFixed(4)).join(',')
            return `${idx}:${leadSamples.length}:${probes}`
        })
        return `12lead|${parts.join('|')}`
    }
    const beatType = beatTypeOverride ?? annotatorState.settings?.medianBeatBeatType ?? 'n'
    const beats = annotatorState.annotations?.beats || []
    const matchingIndices = beats
        .filter(b => b.t === beatType)
        .map(b => b.i)
        .sort((a, b) => a - b)
    const filteredLen = annotatorState.filtered?.samples?.II?.length
        ?? Object.values(annotatorState.filtered?.samples || {})[0]?.length
        ?? 0
    const inverted = !!annotatorState.settings?.inverted
    return `beats|t:${beatType}|${matchingIndices.join(',')}|len:${filteredLen}|inv:${inverted ? 1 : 0}`
}

const findOffsetMs = (points, slug) => points.find(p => p.type === slug)?.offsetMs

// Returns { p, pr, qrs, st, qt } in ms. Any interval whose endpoints are missing
// or whose duration is non-positive is returned as null. Points are stored in
// ms relative to the R-peak, so intervals are simple subtraction.
export const computeFiducialIntervals = (fiducialPoints) => {
    const empty = { p: null, pr: null, qrs: null, st: null, qt: null }
    if (!fiducialPoints) return empty

    const pOnset = findOffsetMs(fiducialPoints, 'p_onset')
    const pOffset = findOffsetMs(fiducialPoints, 'p_offset')
    const qrsOnset = findOffsetMs(fiducialPoints, 'qrs_onset')
    const qrsOffset = findOffsetMs(fiducialPoints, 'qrs_offset')
    const tOffset = findOffsetMs(fiducialPoints, 't_offset')

    const interval = (start, end) => {
        if (start == null || end == null) return null
        const diff = end - start
        if (diff <= 0) return null
        return diff
    }

    return {
        p: interval(pOnset, pOffset),
        pr: interval(pOnset, qrsOnset),
        qrs: interval(qrsOnset, qrsOffset),
        st: interval(qrsOffset, tOffset),
        qt: interval(qrsOnset, tOffset),
    }
}
